import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MaskLine, Reveal, ClipReveal } from "@/components/Reveal";
import { api, resolveImg } from "@/lib/api";
import ReachMap from "@/components/ReachMap";

const WHY = [
  { n: "01", t: "In-House Manufacturing", d: "Our own factory at TTC Industrial Area gives us full control over quality, cost and delivery timelines." },
  { n: "02", t: "Turnkey Execution", d: "From design and engineering to installation — a single accountable partner for the entire project." },
  { n: "03", t: "Built to Last", d: "Industrial-grade materials and rigorous QC ensure every solution performs for years, not months." },
  { n: "04", t: "Trusted by Industry", d: "Delivered for India's leading industrial sectors." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [pr, cl, rc] = await Promise.all([
          api.get("/products"),
          api.get("/clients"),
          api.get("/reach"),
        ]);
        setProducts(pr.data || []);
        setClients(cl.data || []);
        setLocations(rc.data || []);
      } catch {
        /* silent */
      }
    })();
  }, []);

  const indiaCount = locations.filter((l) => l.region === "India").length;
  const intlCount = locations.filter((l) => l.region !== "India").length;
  const uaeLocs = locations.filter((l) => (l.country || "").toUpperCase() === "UAE");
  const usaLocs = locations.filter((l) => (l.country || "").toUpperCase() === "USA");

  return (
    <div data-testid="home-page" className="bg-white">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0e5f84]">
        <motion.video
          style={{ scale }}
          autoPlay loop muted playsInline preload="auto"
          poster="/assets/hero-poster.jpg"
          data-testid="hero-video"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/assets/hero.mp4" type="video/mp4" />
        </motion.video>
        <div className="absolute inset-0 bg-[#0e5f84]/60" />

        <motion.div style={{ y }} className="relative z-10 mx-auto max-w-[1100px] w-full px-6 lg:px-10 pt-36 pb-20 text-center">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#78be27] mb-8"
          >
            Manik Engineering Pvt. Ltd. — Est. Mumbai
          </motion.p>
          <h1 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
            <MaskLine delay={0.15}>We engineer</MaskLine>
            <MaskLine delay={0.3}>
              <span className="text-[#78be27]">modular</span> spaces
            </MaskLine>
            <MaskLine delay={0.45}>industry <span className="text-[#78be27]">trusts.</span></MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-10 text-white/85 max-w-xl mx-auto leading-relaxed text-lg"
          >
            In-plant offices, partitions, warehouse safety, furniture and industrial storage —
            designed, manufactured and installed end-to-end.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/products"
              data-testid="hero-explore-btn"
              className="group inline-flex items-center justify-center gap-3 bg-[#78be27] hover:bg-[#6aa81f] px-8 py-4 font-mono-accent text-xs uppercase tracking-widest text-white transition-colors"
            >
              Explore Products
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-quote-btn"
              className="group inline-flex items-center justify-center gap-3 border border-white/70 hover:border-white px-8 py-4 font-mono-accent text-xs uppercase tracking-widest text-white transition-colors"
            >
              Enquire Now
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#0e5f84] py-6">
        <Marquee speed={40} gradient={false}>
          {["ModulArt In-Plant Offices", "Office Partitions", "Warehouse Safety", "Office Furniture", "Industrial Storage"].map((t, i) => (
            <span key={i} className="font-display font-semibold text-3xl lg:text-5xl text-white mx-8 flex items-center gap-8">
              {t}
              <span className="text-[#78be27]">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* STATS BAND */}
      <section className="bg-[#0e5f84]" data-testid="stats-band">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-24 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-14 gap-x-6 lg:divide-x lg:divide-white/20">
          {/* Certifications first, then existing metrics — Option A order */}
          <Reveal delay={0} className="text-center lg:text-left lg:pl-6 lg:first:pl-0">
            <div data-testid="stat-iso" className="flex flex-col items-center lg:items-start">
              <svg width="72" height="72" viewBox="0 0 100 100" fill="none" aria-hidden>
                <path d="M50 6 L61 12 L74 10 L81 21 L94 27 L94 41 L100 52 L93 63 L94 77 L82 84 L74 96 L61 94 L50 100 L39 94 L26 96 L18 84 L6 77 L7 63 L0 52 L6 41 L6 27 L19 21 L26 10 L39 12 Z" fill="#78be27" />
                <text x="50" y="45" textAnchor="middle" fontFamily="Montserrat" fontWeight="900" fontSize="17" fill="white">ISO</text>
                <text x="50" y="63" textAnchor="middle" fontFamily="Montserrat" fontWeight="700" fontSize="10" fill="white">9001:2015</text>
              </svg>
            </div>
            <p className="mt-4 font-display font-bold uppercase text-white text-base lg:text-lg tracking-wide">ISO 9001:2015 Certified</p>
            <span className="mt-3 block w-10 h-[3px] bg-[#78be27] mx-auto lg:mx-0" />
          </Reveal>
          <Reveal delay={0.05} className="text-center lg:text-left lg:pl-6">
            <p data-testid="stat-star-export" className="font-display font-black text-white leading-[0.9] tracking-tight text-6xl lg:text-7xl inline-flex items-baseline gap-2">
              2 <span className="text-[#78be27] text-3xl lg:text-4xl">★★</span>
            </p>
            <p className="mt-4 font-display font-bold uppercase text-white text-base lg:text-lg tracking-wide">Star Export House</p>
            <span className="mt-3 block w-10 h-[3px] bg-[#78be27] mx-auto lg:mx-0" />
          </Reveal>
          {[["500+", "Projects Delivered"], ["15+", "Years of Expertise"], ["2", "Manufacturing Units"], ["100%", "Turnkey Delivery"]].map(([n, l], i) => (
            <Reveal key={l} delay={0.1 + i * 0.05} className="text-center lg:text-left lg:pl-6">
              <p data-testid={`stat-number-${i}`} className="font-display font-black text-white leading-[0.9] tracking-tight text-6xl lg:text-7xl">{n}</p>
              <p data-testid={`stat-label-${i}`} className="mt-4 font-display font-bold uppercase text-white text-base lg:text-lg tracking-wide">{l}</p>
              <span className="mt-3 block w-10 h-[3px] bg-[#78be27] mx-auto lg:mx-0" />
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO / INTRO */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-28 lg:py-40 bg-white">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8]">01 — Who We Are</p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#334155]">
                A single engineering partner for the modern industrial workspace — from the
                <span className="text-[#78be27]"> factory floor </span>
                to the
                <span className="text-[#3da8d8]"> executive cabin.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-10 text-lg text-[#52525B] max-w-2xl leading-relaxed">
                Manik Engineering designs and manufactures modular office solutions, warehouse safety
                products, office furniture and industrial storage systems. We own the entire process —
                so quality, cost and timelines stay in our hands, and in your interest.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRODUCT OVERVIEW - bento */}
      <section className="bg-[#eaf6fc] border-y border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
            <Reveal>
              <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-4">02 — What We Build</p>
              <h2 className="font-display font-bold text-4xl lg:text-6xl tracking-tight text-[#334155]">Product Categories</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/products" data-testid="overview-all-products" className="font-mono-accent text-xs uppercase tracking-widest link-underline pb-1 inline-flex items-center gap-2 text-[#3da8d8]">
                View all <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-6 gap-4">
            {products.map((p, i) => {
              const span = i === 0 ? "md:col-span-4" : "md:col-span-2";
              return (
                <Reveal key={p.id} delay={i * 0.05} className={span}>
                  <Link
                    to="/products"
                    data-testid={`overview-card-${p.slug}`}
                    className="group block relative overflow-hidden border border-black/10 bg-white h-72 lg:h-80"
                  >
                    <img
                      src={resolveImg(p.image)}
                      alt={p.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-95 group-hover:scale-105 transition-transform duration-[900ms] ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#3da8d8]/95 via-[#3da8d8]/25 to-transparent" />
                    <div className="absolute inset-0 p-7 flex flex-col justify-between">
                      <span className="font-mono-accent text-xs tracking-widest text-white/90">{String(i + 1).padStart(2, "0")}</span>
                      <div>
                        <h3 className="font-display font-semibold text-2xl lg:text-3xl text-white leading-tight">{p.title}</h3>
                        <span className="mt-3 inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-widest text-white translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                          Discover <ArrowUpRight size={12} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-28 bg-white">
        <Reveal>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-4">03 — Why Manik</p>
          <h2 className="font-display font-bold text-4xl lg:text-6xl tracking-tight max-w-3xl text-[#334155]">Built on engineering, delivered with commitment.</h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
          {WHY.map((w, i) => (
            <Reveal key={w.n} delay={i * 0.08} className="bg-white">
              <div className="p-8 h-full hover:bg-[#eaf6fc] transition-colors duration-500 group">
                <p className="font-mono-accent text-sm text-[#78be27] mb-8">{w.n}</p>
                <h3 className="font-display font-semibold text-2xl mb-4 text-[#334155] group-hover:text-[#3da8d8] transition-colors">{w.t}</h3>
                <p className="text-[#52525B] leading-relaxed">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OUR CLIENTS */}
      <section data-testid="home-clients-section" className="bg-white border-t border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24">
          <Reveal>
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-4">04 — Trusted By</p>
            <h2 className="font-display font-bold text-4xl lg:text-6xl tracking-tight text-[#334155]">
              Our clients.
            </h2>
            <p className="mt-6 max-w-2xl text-[#52525B] leading-relaxed text-lg">
              Trusted by leading organizations.
            </p>
          </Reveal>
        </div>
        <div className="border-y border-black/10 bg-[#F9FAFB] py-10">
          {clients.length > 0 ? (
            <Marquee speed={45} gradient gradientColor="rgb(249 250 251)" gradientWidth={80} pauseOnHover>
              {clients.map((cl) => (
                <div
                  key={cl.id}
                  data-testid={`home-client-${cl.id}`}
                  className="mx-10 h-24 w-44 flex items-center justify-center group"
                  title={cl.name}
                >
                  {cl.logo ? (
                    cl.website ? (
                      <a
                        href={cl.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block h-full w-full flex items-center justify-center"
                        aria-label={cl.name}
                      >
                        <img
                          src={resolveImg(cl.logo)}
                          alt={cl.name}
                          className="max-h-16 max-w-full object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                        />
                      </a>
                    ) : (
                      <img
                        src={resolveImg(cl.logo)}
                        alt={cl.name}
                        className="max-h-16 max-w-full object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                      />
                    )
                  ) : (
                    // Placeholder slot when admin hasn't uploaded a logo yet
                    <div className="h-16 w-40 border border-dashed border-black/20 flex items-center justify-center text-[#94A3B8]">
                      <span className="font-mono-accent text-[10px] uppercase tracking-widest">{cl.name || "Logo Slot"}</span>
                    </div>
                  )}
                </div>
              ))}
            </Marquee>
          ) : (
            <p className="text-center font-mono-accent text-xs uppercase tracking-widest text-[#52525B]">
              Client logos coming soon — manage from the Admin panel
            </p>
          )}
        </div>
      </section>

      {/* OUR REACH */}
      <section data-testid="home-reach-section" className="bg-white border-t border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24">
          <Reveal>
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-4">05 — Global Footprint</p>
            <h2 className="font-display font-bold text-4xl lg:text-6xl tracking-tight text-[#334155]">
              Our reach.
            </h2>
            <p className="mt-6 max-w-2xl text-[#52525B] leading-relaxed text-lg">
              Powering industrial infrastructure across diverse sectors and locations.
            </p>
          </Reveal>

          <div className="mt-16 grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3 border border-black/10 bg-[#F0F5F9] overflow-hidden">
              <ReachMap locations={locations} />
            </div>

            <div className="lg:col-span-2 space-y-4">
              <Reveal>
                <div data-testid="reach-stat-india" className="border border-black/10 bg-white p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono-accent text-[10px] uppercase tracking-widest text-[#78be27]">India</span>
                    <span className="w-2.5 h-2.5 rounded-full bg-[#78be27] shadow-[0_0_10px_#78be27]" />
                  </div>
                  <p className="font-display font-black text-5xl lg:text-6xl text-[#334155] leading-none">{indiaCount || 0}<span className="text-2xl align-top ml-1">{indiaCount >= 10 ? "+" : ""}</span></p>
                  <p className="mt-3 text-sm text-[#52525B] leading-relaxed">
                    Cities · {locations.filter((l) => l.region === "India").map((l) => l.city).join(", ") || "Add cities from the Admin panel"}
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <div data-testid="reach-stat-uae" className="border border-black/10 bg-white p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono-accent text-[10px] uppercase tracking-widest text-[#3da8d8]">Middle East</span>
                    <span className="w-2.5 h-2.5 rounded-full bg-[#3da8d8] shadow-[0_0_10px_#3da8d8]" />
                  </div>
                  <p className="font-display font-black text-5xl lg:text-6xl text-[#334155] leading-none">{uaeLocs.length}</p>
                  <p className="mt-3 text-sm text-[#52525B]">{uaeLocs.map((l) => `${l.city} · ${l.country || "UAE"}`).join(", ") || "—"}</p>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div data-testid="reach-stat-usa" className="border border-black/10 bg-white p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono-accent text-[10px] uppercase tracking-widest text-[#3da8d8]">North America</span>
                    <span className="w-2.5 h-2.5 rounded-full bg-[#3da8d8] shadow-[0_0_10px_#3da8d8]" />
                  </div>
                  <p className="font-display font-black text-5xl lg:text-6xl text-[#334155] leading-none">{usaLocs.length}</p>
                  <p className="mt-3 text-sm text-[#52525B]">{usaLocs.map((l) => `${l.city}${l.city !== "USA" ? " · United States" : " · United States of America"}`).join(", ") || "—"}</p>
                </div>
              </Reveal>
              {intlCount + indiaCount === 0 && (
                <p className="text-center font-mono-accent text-[10px] uppercase tracking-widest text-[#94A3B8]">
                  No reach locations yet — add them from the Admin panel
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="relative bg-[#0e5f84] overflow-hidden grain">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-28 lg:py-40 relative z-10">
          <ClipReveal>
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#eaf6d8] mb-8">Start a Project</p>
          </ClipReveal>
          <Reveal>
            <h2 className="font-display font-bold text-white text-4xl sm:text-6xl lg:text-8xl tracking-tight leading-[0.98] max-w-4xl">
              Let&apos;s build your next <span className="text-[#78be27]">workspace.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="btn-fill group mt-12 inline-flex items-center gap-4 border border-white px-10 py-5 font-mono-accent text-sm uppercase tracking-widest text-white"
            >
              Enquire Now
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
